import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FamlaServices from '../../../services/api/FamlaServices';
import { useAppSelector } from '../../../stores';

interface Campaign {
    _id: string
    name: string
    description: string
    objective: string
    status: string
    created_at: string
    updated_at: string
    creator_id: string
    company_id: string
    industrie_id?: string
    process_ids?: string[]
}

interface CampaignOperationProps {
    campaignId: string;
    campaignName: string;
    onUpdated: (campaign: Campaign) => void;
    onDeleted: (campaignId: string) => void;
}

const CampaignOperation: React.FC<CampaignOperationProps> = ({
    campaignId,
    campaignName,
    onUpdated,
    onDeleted
}) => {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [formData, setFormData] = useState({ name: campaignName });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                // Close dropdown if clicking outside
                const dropdownElement = dropdownRef.current.querySelector('[data-bs-toggle="dropdown"]') as HTMLElement;
                if (dropdownElement) {
                    const bsDropdown = (window as any).bootstrap?.Dropdown?.getInstance(dropdownElement);
                    if (bsDropdown) {
                        bsDropdown.hide();
                    }
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (showUpdateModal) {
            setFormData({ name: campaignName });
            setError(null);
        }
    }, [showUpdateModal, campaignName]);

    const handleUpdateClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Close dropdown
        const dropdownElement = dropdownRef.current?.querySelector('[data-bs-toggle="dropdown"]') as HTMLElement;
        if (dropdownElement) {
            const bsDropdown = (window as any).bootstrap?.Dropdown?.getInstance(dropdownElement);
            if (bsDropdown) {
                bsDropdown.hide();
            }
        }
        setShowUpdateModal(true);
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        // Close dropdown
        const dropdownElement = dropdownRef.current?.querySelector('[data-bs-toggle="dropdown"]') as HTMLElement;
        if (dropdownElement) {
            const bsDropdown = (window as any).bootstrap?.Dropdown?.getInstance(dropdownElement);
            if (bsDropdown) {
                bsDropdown.hide();
            }
        }

        if (window.confirm(t('campaigns.confirmDelete'))) {
            try {
                const data = await FamlaServices(
                    "api_host",
                    `/campaign/${campaignId}`,
                    "DELETE",
                    "",
                    users.access_token
                );

                if (data.status === 200) {
                    onDeleted(campaignId);
                } else {
                    alert(t('campaigns.deleteError'));
                }
            } catch (error) {
                console.error('Error deleting campaign:', error);
                alert(t('campaigns.deleteError'));
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(null);
    };

    const handleCloseModal = () => {
        setFormData({ name: campaignName });
        setError(null);
        setShowUpdateModal(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        try {
            const data = await FamlaServices(
                "api_host",
                `/campaign/${campaignId}`,
                "PUT",
                { name: formData.name },
                users.access_token
            );

            if (data.status === 200 && data.body.data) {
                onUpdated(data.body.data);
                handleCloseModal();
            } else {
                setError(data.body.description || t('campaigns.updateError'));
            }
        } catch (error) {
            console.error('Error updating campaign:', error);
            setError(t('campaigns.updateError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div 
                className="dropdown" 
                ref={dropdownRef}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className="btn btn-link text-light p-0 border-0"
                    type="button"
                    id={`campaign-menu-${campaignId}`}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ fontSize: '0.9rem', lineHeight: '1' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <i className="fa-solid fa-ellipsis-vertical w-4"></i>
                </button>
                <ul 
                    className="dropdown-menu dropdown-menu-dark small border border-secondary"
                    aria-labelledby={`campaign-menu-${campaignId}`}
                >
                    <li>
                        <button 
                            onClick={handleUpdateClick} 
                            className="dropdown-item text-decoration-none border-0"
                        >
                            <i className="fa-solid fa-pen me-2"></i>
                            {t('campaigns.update')}
                        </button>
                    </li>
                    <li>
                        <button 
                            onClick={handleDelete} 
                            className="dropdown-item text-decoration-none border-0 text-danger"
                        >
                            <i className="fa-solid fa-trash me-2"></i>
                            {t('campaigns.delete')}
                        </button>
                    </li>
                </ul>
            </div>

            {/* Update Campaign Modal */}
            {showUpdateModal && (
                <div 
                    className="modal fade show" 
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
                    onClick={handleCloseModal}
                >
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content bg-dark border border-secondary">
                            <div className="modal-header border-secondary">
                                <h5 className="modal-title text-light">
                                    {t('campaigns.updateCampaign')}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={handleCloseModal}
                                    aria-label="Close"
                                ></button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    {error && (
                                        <div className="alert alert-danger" role="alert">
                                            {error}
                                        </div>
                                    )}

                                    <div className="mb-3 d-flex align-items-center gap-3">
                                        <label htmlFor={`name-${campaignId}`} className="form-label text-light mb-0" style={{ minWidth: '100px' }}>
                                            {t('campaigns.name')} <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id={`name-${campaignId}`}
                                            name="name"
                                            className="form-control bg-dark text-light border-secondary flex-grow-1"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder={t('campaigns.namePlaceholder')}
                                            required
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>

                                <div className="modal-footer border-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleCloseModal}
                                        disabled={isSubmitting}
                                    >
                                        {t('words.close')}
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {t('campaigns.updating')}
                                            </>
                                        ) : (
                                            t('campaigns.update')
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CampaignOperation;

